import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useOutletContext } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import SpaceAccessManager from '../components/settings/SpaceAccessManager';
import PublishingSettings from '../components/settings/PublishingSettings';
import BrandingSettings from '../components/settings/BrandingSettings';
import EmbedSettings from '../components/EmbedSettings';
import CustomDomainSettings from '../components/spaces/CustomDomainSettings';
import BillingSettings from '../components/settings/BillingSettings';
import { Settings, Building2, Trash2, Loader2, Lock, FolderLock, Globe, Palette, Code, Link2, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { useOrgSubscription } from '../hooks/useOrgSubscription';
import { API_URL } from '../config';

const SettingsPage = () => {
  const { currentOrg, selectOrganization } = useAuth();
  const { fetchOrganizations } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [name, setName] = useState(currentOrg?.name || '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [selectedSpace, setSelectedSpace] = useState('');
  const [loadingSpaces, setLoadingSpaces] = useState(false);
  const initialTab = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState(initialTab || 'general');
  const { loading: subLoading, features } = useOrgSubscription(currentOrg?.id);

  // Handle URL params for tab and space pre-selection
  useEffect(() => {
    const tab = searchParams.get('tab');
    const spaceParam = searchParams.get('space');
    
    if (tab) {
      setActiveTab(tab);
    }
    if (spaceParam && spaces.length > 0) {
      const spaceExists = spaces.some(s => s.id === spaceParam);
      if (spaceExists) {
        setSelectedSpace(spaceParam);
      }
    }
  }, [searchParams, spaces]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    const newParams = new URLSearchParams(searchParams);
    newParams.set('tab', tab);
    setSearchParams(newParams);
  };

  const isOwner = currentOrg?.user_role === 'company';
  const canEdit = currentOrg?.user_role === 'company';

  const fetchSpaces = useCallback(async () => {
    if (!currentOrg) return;
    
    setLoadingSpaces(true);
    try {
      const response = await axios.get(`${API_URL}/organizations/${currentOrg.id}/spaces`);
      setSpaces(response.data);
      // Select the first space by default if none selected
      if (response.data.length > 0 && !selectedSpace) {
        setSelectedSpace(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch spaces:', error);
    } finally {
      setLoadingSpaces(false);
    }
  }, [currentOrg, selectedSpace]);

  useEffect(() => {
    fetchSpaces();
  }, [currentOrg?.id]);

  // Update name when org changes
  useEffect(() => {
    setName(currentOrg?.name || '');
  }, [currentOrg?.name]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!name.trim() || !currentOrg) return;

    setSaving(true);
    try {
      const response = await axios.put(`${API_URL}/organizations/${currentOrg.id}`, { name });
      selectOrganization(response.data);
      await fetchOrganizations();
      toast.success('Workspace updated');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update workspace';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentOrg) return;

    setDeleting(true);
    try {
      await axios.delete(`${API_URL}/organizations/${currentOrg.id}`);
      selectOrganization(null);
      await fetchOrganizations();
      toast.success('Workspace deleted');
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete workspace';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  if (!currentOrg) {
    return (
      <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="settings-page">
        <div className="mb-8">
          <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">
            Settings
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Select a workspace to manage settings
          </p>
        </div>
        <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Settings className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">
              No Workspace Selected
            </h3>
            <p className="text-slate-600 dark:text-slate-400">
              Please select a workspace from the sidebar.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8 lg:p-12 animate-fade-in" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold font-[Manrope] text-slate-900 dark:text-white mb-2">
          Settings
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          Manage your workspace settings
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange} className="space-y-6">
        <TabsList className="flex-wrap">
          <TabsTrigger value="general" className="flex items-center gap-2">
            <Building2 className="w-4 h-4" />
            General
          </TabsTrigger>
          {canEdit && (
            <TabsTrigger value="access" className="flex items-center gap-2">
              <FolderLock className="w-4 h-4" />
              Space Access
            </TabsTrigger>
          )}
          {canEdit && (
            <TabsTrigger value="publishing" className="flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Publishing
            </TabsTrigger>
          )}
          {canEdit && (
            <TabsTrigger value="branding" className="flex items-center gap-2">
              <Palette className="w-4 h-4" />
              Branding
            </TabsTrigger>
          )}
          {canEdit && (
            <TabsTrigger value="embed" className="flex items-center gap-2">
              <Code className="w-4 h-4" />
              Embed
            </TabsTrigger>
          )}
          {canEdit && (
            <TabsTrigger value="domain" className="flex items-center gap-2">
              <Link2 className="w-4 h-4" />
              Domain
            </TabsTrigger>
          )}
          {isOwner && (
            <TabsTrigger value="billing" className="flex items-center gap-2">
              <CreditCard className="w-4 h-4" />
              Billing
            </TabsTrigger>
          )}
        </TabsList>

        {/* General Settings Tab */}
        <TabsContent value="general" className="space-y-6">
          <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
            <CardHeader>
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20">
                  <Building2 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                    General Settings
                  </CardTitle>
                  <CardDescription>
                    Basic workspace information
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSave} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="workspace-name">Workspace Name</Label>
                  <Input
                    id="workspace-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="My Company Docs"
                    disabled={!canEdit}
                    data-testid="workspace-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Workspace Slug</Label>
                  <Input
                    value={currentOrg.slug}
                    disabled
                    className="bg-slate-50 dark:bg-slate-800"
                  />
                  <p className="text-xs text-slate-500">
                    The slug is used in URLs and cannot be changed
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Created</Label>
                  <Input
                    value={new Date(currentOrg.created_at).toLocaleDateString()}
                    disabled
                    className="bg-slate-50 dark:bg-slate-800"
                  />
                </div>
                {canEdit ? (
                  <Button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={saving || name === currentOrg.name}
                    data-testid="save-settings-button"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save Changes'
                    )}
                  </Button>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Lock className="w-4 h-4" />
                    <span>Only admins and owners can edit settings</span>
                  </div>
                )}
              </form>
            </CardContent>
          </Card>

          {/* Danger Zone */}
          {isOwner && (
            <Card className="bg-white dark:bg-slate-900 border-red-200 dark:border-red-900/50">
              <CardHeader>
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-red-50 dark:bg-red-900/20">
                    <Trash2 className="w-5 h-5 text-red-600 dark:text-red-400" />
                  </div>
                  <div>
                    <CardTitle className="font-[Manrope] text-red-600 dark:text-red-400">
                      Danger Zone
                    </CardTitle>
                    <CardDescription>
                      Irreversible and destructive actions
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="p-4 rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50/50 dark:bg-red-900/10">
                  <h4 className="font-medium text-slate-900 dark:text-white mb-2">
                    Delete Workspace
                  </h4>
                  <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
                    Once you delete a workspace, there is no going back. All data, spaces, and member associations will be permanently removed.
                  </p>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="destructive"
                        className="bg-red-600 hover:bg-red-700"
                        data-testid="delete-workspace-button"
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Delete Workspace
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This action cannot be undone. This will permanently delete the
                          workspace <strong>{currentOrg.name}</strong> and remove all associated data.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-red-600 hover:bg-red-700"
                          disabled={deleting}
                          data-testid="confirm-delete-button"
                        >
                          {deleting ? (
                            <>
                              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                              Deleting...
                            </>
                          ) : (
                            'Yes, delete workspace'
                          )}
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Space Access Tab */}
        {canEdit && (
          <TabsContent value="access" className="space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                  Select Space
                </CardTitle>
                <CardDescription>
                  Choose a space to manage access controls
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSpaces ? (
                  <div className="py-4 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : spaces.length > 0 ? (
                  <Select value={selectedSpace} onValueChange={setSelectedSpace}>
                    <SelectTrigger data-testid="select-space-trigger">
                      <SelectValue placeholder="Select a space" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaces.map(space => (
                        <SelectItem key={space.id} value={space.id}>
                          {space.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    No spaces found. Create a space first.
                  </p>
                )}
              </CardContent>
            </Card>

            {selectedSpace && (
              <SpaceAccessManager
                spaceId={selectedSpace}
                spaceName={spaces.find(s => s.id === selectedSpace)?.name || 'Space'}
                orgId={currentOrg.id}
              />
            )}
          </TabsContent>
        )}

        {/* Publishing Tab */}
        {canEdit && (
          <TabsContent value="publishing" className="space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                  Select Space to Publish
                </CardTitle>
                <CardDescription>
                  Choose a space to configure its publishing settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSpaces ? (
                  <div className="py-4 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : spaces.length > 0 ? (
                  <Select value={selectedSpace} onValueChange={setSelectedSpace}>
                    <SelectTrigger data-testid="select-space-publish-trigger">
                      <SelectValue placeholder="Select a space" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaces.map(space => (
                        <SelectItem key={space.id} value={space.id}>
                          {space.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    No spaces found. Create a space first.
                  </p>
                )}
              </CardContent>
            </Card>

            {selectedSpace && (
              <PublishingSettings
                spaceId={selectedSpace}
                spaceName={spaces.find(s => s.id === selectedSpace)?.name || 'Space'}
                orgId={currentOrg.id}
                hasCustomBranding={subLoading ? true : features?.custom_branding === true}
              />
            )}
          </TabsContent>
        )}

        {/* Branding Tab */}
        {canEdit && (
          <TabsContent value="branding" className="space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                  Select Space to Brand
                </CardTitle>
                <CardDescription>
                  Choose a space to customize its branding and appearance
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSpaces ? (
                  <div className="py-4 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : spaces.length > 0 ? (
                  <Select value={selectedSpace} onValueChange={setSelectedSpace}>
                    <SelectTrigger data-testid="select-space-branding-trigger">
                      <SelectValue placeholder="Select a space" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaces.map(space => (
                        <SelectItem key={space.id} value={space.id}>
                          {space.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    No spaces found. Create a space first.
                  </p>
                )}
              </CardContent>
            </Card>

            {selectedSpace && (
              <BrandingSettings
                spaceId={selectedSpace}
                spaceName={spaces.find(s => s.id === selectedSpace)?.name || 'Space'}
                orgId={currentOrg.id}
                hasCustomCssJs={subLoading ? true : features?.custom_css_js === true}
              />
            )}
          </TabsContent>
        )}

        {/* Embed Tab */}
        {canEdit && (
          <TabsContent value="embed" className="space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                  Select Space to Embed
                </CardTitle>
                <CardDescription>
                  Choose a space to configure its embed settings
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSpaces ? (
                  <div className="py-4 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : spaces.length > 0 ? (
                  <Select value={selectedSpace} onValueChange={setSelectedSpace}>
                    <SelectTrigger data-testid="select-space-embed-trigger">
                      <SelectValue placeholder="Select a space" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaces.map(space => (
                        <SelectItem key={space.id} value={space.id}>
                          {space.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    No spaces found. Create a space first.
                  </p>
                )}
              </CardContent>
            </Card>

            {selectedSpace && (
              <EmbedSettings
                organizationId={currentOrg.id}
                spaceId={selectedSpace}
                spaceName={spaces.find(s => s.id === selectedSpace)?.name || 'Space'}
              />
            )}
          </TabsContent>
        )}

        {/* Domain Tab */}
        {canEdit && (
          <TabsContent value="domain" className="space-y-6">
            <Card className="bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800">
              <CardHeader>
                <CardTitle className="font-[Manrope] text-slate-900 dark:text-white">
                  Select Space for Custom Domain
                </CardTitle>
                <CardDescription>
                  Choose a space to configure its custom domain
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingSpaces ? (
                  <div className="py-4 text-center">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-slate-400" />
                  </div>
                ) : spaces.length > 0 ? (
                  <Select value={selectedSpace} onValueChange={setSelectedSpace}>
                    <SelectTrigger data-testid="select-space-domain-trigger">
                      <SelectValue placeholder="Select a space" />
                    </SelectTrigger>
                    <SelectContent>
                      {spaces.map(space => (
                        <SelectItem key={space.id} value={space.id}>
                          {space.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-slate-500 dark:text-slate-400 text-sm">
                    No spaces found. Create a space first.
                  </p>
                )}
              </CardContent>
            </Card>

            {selectedSpace && (
              <CustomDomainSettings
                organizationId={currentOrg.id}
                spaceId={selectedSpace}
                spaceName={spaces.find(s => s.id === selectedSpace)?.name || 'Space'}
              />
            )}
          </TabsContent>
        )}

        {isOwner && (
          <TabsContent value="billing" className="space-y-6">
            <BillingSettings orgId={currentOrg.id} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default SettingsPage;
